import { useState } from "react";
import { ArrowLeft, Printer, Download, ChevronsUpDown } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { WeeklyChart } from "./WeeklyChart";
import { PrintHeader } from "./PrintHeader";
import { WeeklyTable } from "./WeeklyTable";
import { fmtNum } from "../lib/format";
import { fmtClock, fmtDur, sheetCsv } from "../lib/pickerSheet";

const Card = ({ label, value, sub, testId, tone = "text-slate-100" }) => (
  <div className="panel p-4" data-testid={testId}>
    <p className="label-mono">{label}</p>
    <p className={`mt-3 font-mono text-2xl ${tone}`}>{value}</p>
    {sub && <p className="text-[11px] text-slate-500 font-mono mt-1">{sub}</p>}
  </div>
);

const download = (user) => {
  const url = URL.createObjectURL(new Blob([sheetCsv(user)], { type: "text/csv" }));
  const a = Object.assign(document.createElement("a"), { href: url, download: `picker-sheet-${user.id}.csv` });
  a.click();
  URL.revokeObjectURL(url);
};
const btn = "inline-flex items-center gap-2 text-xs px-3 py-2 rounded-md border border-slate-700 text-slate-300 hover:bg-slate-800";

export const PickerDetail = ({ users, user, cfg, onSelect, onBack }) => {
  const [expand, setExpand] = useState(false);
  const print = () => {
    const el = document.documentElement;
    const wasDark = !el.classList.contains("light");
    setExpand(true);
    el.classList.add("light");
    setTimeout(() => { window.print(); if (wasDark) el.classList.remove("light"); }, 400);
  };
  return (
  <div className="space-y-6 print-area" data-testid="picker-detail">
    <PrintHeader title={`Picker sheet · ${user.id}`} subtitle={`Shifts ${cfg.shifts.map((s) => `${s.name} ${s.start}–${s.end}`).join(", ")} · gap ≥ ${cfg.gapMin} min · lunch ${cfg.breakMin} min`} />
    <div className="flex flex-wrap items-center gap-3">
      <button data-testid="sheet-back-button" onClick={onBack} className={`${btn} no-print`}><ArrowLeft className="h-3.5 w-3.5" /> All pickers</button>
      <div className="no-print"><Select value={user.id} onValueChange={onSelect}>
        <SelectTrigger data-testid="picker-select" className="h-9 w-[220px] bg-[#0B0F19] border-slate-700 text-slate-100"><SelectValue /></SelectTrigger>
        <SelectContent className="max-h-80">{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.id}</SelectItem>)}</SelectContent>
      </Select></div>
      <h2 className="font-display text-2xl font-bold text-slate-100 ml-1" data-testid="picker-detail-name">{user.id} <span className="text-sm font-normal text-slate-400">· {user.mainShift} shift</span></h2>
      <div className="ml-auto flex gap-2 no-print">
        <button data-testid="sheet-expand-all" onClick={() => setExpand(!expand)} className={btn}><ChevronsUpDown className="h-3.5 w-3.5" /> {expand ? "Collapse days" : "Expand days"}</button>
        <button data-testid="sheet-download-csv" onClick={() => download(user)} className={btn}><Download className="h-3.5 w-3.5" /> Download CSV</button>
        <button data-testid="sheet-print" onClick={print} className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-md bg-ep hover:bg-ep-hover text-white"><Printer className="h-3.5 w-3.5" /> Print / PDF</button>
      </div>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
      <Card label="Avg picks / week" value={fmtNum(Math.round(user.avgPicksWk))} sub={`${fmtNum(user.picks)} total · ${user.weeks.length} wks`} testId="detail-avg-picks-wk" />
      <Card label="Avg hours / week" value={user.avgHoursWk.toFixed(1)} sub={`${user.daysWorked} days worked`} testId="detail-avg-hours-wk" tone="text-amber-300" />
      <Card label="Picks / hr worked" value={fmtNum(user.pph)} testId="detail-pph" tone="text-cyan-300" />
      <Card label="Avg start / finish" value={`${fmtClock(user.avgStart)}–${fmtClock(user.avgFinish)}`} testId="detail-start-finish" />
      <Card label="Avg lost / day" value={fmtDur(user.avgLostDay)} sub={`${fmtDur(user.lost)} total`} testId="detail-avg-lost" tone="text-rose-300" />
      <Card label="Large gaps" value={fmtNum(user.gapCount)} sub={`${fmtDur(user.gapTime)} idle in shift`} testId="detail-gap-count" tone="text-rose-300" />
    </div>
    <WeeklyChart weeks={user.weeks} />
    <WeeklyTable key={user.id} weeks={user.weeks} expandAll={expand} />
  </div>
  );
};
